/**
 * Seed data for the in-memory mock backend: platform settings, the email template catalogue,
 * the email layout, the image library and a couple of broadcasts.
 *
 * These mirror the shapes the API's own seed writes, so every email page in the console is
 * fully clickable offline.
 *
 * ## Templates hold a body now, not a document
 *
 * Each template used to be a complete HTML document, which meant twelve copies of the same
 * header, card and footer. The chrome moved into the email layout, so what a template row
 * carries is its middle: a heading, some paragraphs, a button, a table of receipt rows. The
 * layout draws everything around it, and one edit there re-skins all twelve messages.
 *
 * That is also why the bodies below paint with `{{layoutHeading}}` / `{{layoutBody}}` /
 * `{{layoutMuted}}` tokens rather than hex literals. A body with `color:#101828` written into
 * every heading looks fine until an operator picks a dark card, at which point it is dark text
 * on a dark background and nothing in the system notices: the palette validator guards the
 * layout's own pairs, and it cannot guard a literal somebody typed into a body. Tokens are what
 * make that validator cover the whole message.
 */
import type {
  EmailBroadcastDetail,
  EmailImage,
  EmailLayout,
  EmailTemplate,
  PlatformSettings,
} from '../../contracts/types.js';
// The console's own mirror of the contract's palette default and logo bounds. Imported rather
// than copied a third time: this console bundles `@genex/contracts` for types only, so the
// runtime values live in `lib/emailLayout.ts`, where a test holds them against the contract by
// source path. A fourth copy here would be the one that drifts.
import { DEFAULT_EMAIL_LOGO_WIDTH, DEFAULT_EMAIL_PALETTE } from '../../lib/emailLayout.js';

const SUPERADMIN_ID = 'a1000000-0000-4000-8000-000000000001';
const SEED_AT = '2026-07-01T09:00:00.000Z';

/** The single white-label config row, pinned to id 1 in the real schema. */
export function seedPlatformSettings(): PlatformSettings {
  return {
    platformName: 'GenEx Pay',
    logoAssetKey: null,
    splashAssetKey: null,
    iconAssetKey: null,
    heroNairaAssetKey: null,
    heroCryptoAssetKey: null,
    // Defaults, matching the column's own. The mock must not imply a choice nobody made.
    heroNairaTextColor: 'auto',
    heroCryptoTextColor: 'auto',
    primaryColor: '#5B2CE6',
    accentColor: '#16C784',
    supportEmail: 'support@genex.pay',
    contactEmail: 'hello@genex.pay',
    // Its own address rather than a reuse of `supportEmail`: a stock refusal delivered into
    // the public support inbox is an alert that arrives correctly and is read by nobody.
    adminNotifyEmail: 'ops@genex.pay',
    deviceOtpEmailEnabled: true,
    deviceOtpSmsEnabled: true,
    websiteUrl: 'https://genex.pay',
    termsUrl: 'https://genex.pay/terms',
    privacyUrl: 'https://genex.pay/privacy',
    social: {
      twitter: 'https://twitter.com/genexpay',
      instagram: 'https://instagram.com/genexpay',
    },
    defaultCurrency: 'NGN',
    // Mirrors the API's `DEFAULT_CONVERSION_CURRENCIES`. USD is absent on purpose: there
    // is no dollar cash rail, so offering a USD conversion would let someone move a
    // balance with no funding or withdrawal path of its own.
    conversionCurrencies: ['NGN', 'BTC', 'ETH', 'SOL', 'USDT'],
    updatedByAdminId: null,
    updatedAt: SEED_AT,
  };
}

// ── The shared type scale ──────────────────────────────────────────────────────
//
// Inline styles on every element, because email has no stylesheet worth relying on: Gmail
// drops `<style>` when a message is forwarded or clipped and Outlook renders with Word's
// engine. These constants are how twelve bodies stay consistent without one.

const H1 =
  'margin:0 0 16px 0;font-size:22px;line-height:30px;font-weight:700;color:{{layoutHeading}};';
const P = 'margin:0 0 16px 0;font-size:15px;line-height:24px;color:{{layoutBody}};';
const MUTED = 'margin:16px 0 0 0;font-size:13px;line-height:20px;color:{{layoutMuted}};';

/**
 * The hairline on a receipt table and the panel borders.
 *
 * `{{layoutMuted}}` rather than a grey literal, and that is a deliberate compromise: the
 * palette has no divider colour of its own, and the muted tone is the closest thing to one
 * that the legibility check actually validates. A literal here would be the exact hole the
 * tokens exist to close.
 */
const HAIRLINE = '{{layoutMuted}}';

const h1 = (text: string): string => `<h1 style="${H1}">${text}</h1>`;
const p = (text: string): string => `<p style="${P}">${text}</p>`;
const muted = (text: string): string => `<p style="${MUTED}">${text}</p>`;

/**
 * The primary call to action.
 *
 * A table rather than a styled anchor, with the padding on the `<a>` rather than on the cell,
 * because that combination is the one Outlook renders as a button instead of as underlined
 * text on a coloured rectangle.
 */
const button = (label: string, href: string): string =>
  `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 20px 0;"><tr><td align="center" bgcolor="{{layoutButtonBg}}" style="background-color:{{layoutButtonBg}};border-radius:10px;mso-padding-alt:14px 28px;"><a href="${href}" style="display:inline-block;padding:14px 28px;font-size:15px;font-weight:600;color:{{layoutButtonText}};text-decoration:none;border-radius:10px;">${label}</a></td></tr></table>`;

/** A one-time code, large and spaced so it can be read off one screen and typed into another. */
const codePanel = (value: string): string =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 20px 0;"><tr><td align="center" bgcolor="{{layoutCardBg}}" style="background-color:{{layoutCardBg}};border:1px solid ${HAIRLINE};border-radius:12px;padding:22px;"><span style="font-size:32px;font-weight:700;letter-spacing:10px;color:{{layoutHeading}};font-family:'SFMono-Regular',Consolas,Menlo,monospace;">${value}</span></td></tr></table>`;

/** One label/value line of a receipt. `emphasise` paints the figure that matters. */
const row = (label: string, value: string, emphasise = false): string => {
  const valueStyle = emphasise
    ? 'font-size:15px;font-weight:700;color:{{accentColor}};'
    : 'font-size:15px;font-weight:600;color:{{layoutHeading}};';
  return `<tr><td style="padding:11px 0;border-bottom:1px solid ${HAIRLINE};font-size:14px;color:{{layoutMuted}};">${label}</td><td align="right" style="padding:11px 0;border-bottom:1px solid ${HAIRLINE};${valueStyle}">${value}</td></tr>`;
};

/** The receipt block. Rows are `[label, value]`, optionally `[label, value, emphasise]`. */
const details = (rows: ReadonlyArray<readonly [string, string, boolean?]>): string =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 20px 0;border:1px solid ${HAIRLINE};border-radius:12px;padding:4px 16px;">${rows
    .map(([label, value, emphasise]) => row(label, value, emphasise ?? false))
    .join('')}</table>`;

/** A quoted message from a person: a support reply, a customer's first chat line. */
const quote = (text: string): string =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 20px 0;"><tr><td bgcolor="{{layoutCardBg}}" style="background-color:{{layoutCardBg}};border-left:3px solid {{primaryColor}};border-radius:8px;padding:16px 18px;font-size:15px;line-height:23px;color:{{layoutBody}};">${text}</td></tr></table>`;

/**
 * One shipped template, before the row fields the store adds.
 *
 * The variable names deliberately stay inside the key set `SAMPLE_VARS` covers, so a preview
 * in the demo fills every placeholder and `missingVariables` comes back empty. A seed that
 * declared variables nothing could fill would teach an operator that the missing-variable
 * warning is noise, which is the one thing that warning must never become.
 */
interface Def {
  readonly key: EmailTemplate['key'];
  readonly name: string;
  readonly subject: string;
  readonly preheader: string;
  readonly variables: readonly string[];
  readonly body: string;
}

const DEFS: readonly Def[] = [
  {
    key: 'welcome',
    name: 'Welcome',
    subject: 'Welcome to {{platformName}}, {{firstName}}',
    preheader: 'Your account is ready to use.',
    variables: ['firstName'],
    body:
      h1('Welcome aboard, {{firstName}}') +
      p(
        'Your {{platformName}} account is ready. Fund your wallet, send money, pay bills and trade, all in one place.',
      ) +
      button('Open {{platformName}}', '{{websiteUrl}}') +
      muted('If you did not create this account, contact us at {{supportEmail}}.'),
  },
  {
    key: 'email_otp',
    name: 'Email verification code',
    subject: 'Your {{platformName}} verification code',
    preheader: 'Use this code to verify your email address.',
    variables: ['code'],
    body:
      h1('Verify your email') +
      p('Use the code below to finish signing in. It expires in 10 minutes.') +
      codePanel('{{code}}') +
      p('Never share this code with anyone. {{platformName}} will never ask you for it.'),
  },
  {
    key: 'password_reset',
    name: 'Password reset',
    subject: 'Reset your {{platformName}} password',
    preheader: 'Choose a new password.',
    variables: ['resetUrl'],
    body:
      h1('Reset your password') +
      p(
        'We received a request to reset your password. Choose a new one with the button below. The link expires in 30 minutes and can be used once.',
      ) +
      button('Reset password', '{{resetUrl}}') +
      muted('If you did not request this, ignore this email. Your password will not change.'),
  },
  {
    key: 'receipt_sell',
    name: 'Receipt: sell crypto',
    subject: 'You sold {{asset}}, receipt {{reference}}',
    preheader: 'Your sale is complete. Here is your receipt.',
    variables: ['amount', 'asset', 'reference'],
    body:
      h1('Sale complete') +
      p('Your crypto sale has settled to your wallet. Here is your receipt.') +
      details([
        ['Sold', '{{asset}}'],
        ['You received', '{{amount}}', true],
        ['Reference', '{{reference}}'],
      ]) +
      muted('Questions about this transaction? Contact {{supportEmail}} and quote the reference.'),
  },
  {
    key: 'receipt_convert',
    name: 'Receipt: convert',
    subject: 'Conversion complete, receipt {{reference}}',
    preheader: 'Your conversion is complete. Here is your receipt.',
    variables: ['fromAmount', 'toAmount', 'reference'],
    body:
      h1('Conversion complete') +
      p('Your funds have been converted and are available now.') +
      details([
        ['Converted', '{{fromAmount}}'],
        ['Received', '{{toAmount}}', true],
        ['Reference', '{{reference}}'],
      ]) +
      muted('Questions about this transaction? Contact {{supportEmail}} and quote the reference.'),
  },
  {
    key: 'receipt_withdraw',
    name: 'Receipt: withdrawal',
    subject: 'Withdrawal sent, receipt {{reference}}',
    preheader: 'Your withdrawal is on its way.',
    variables: ['amount', 'bankName', 'reference'],
    body:
      h1('Withdrawal sent') +
      p('Your payout has been sent to your bank. Here is your receipt.') +
      details([
        ['Amount', '{{amount}}', true],
        ['Bank', '{{bankName}}'],
        ['Reference', '{{reference}}'],
      ]) +
      muted('If you did not authorise this withdrawal, contact {{supportEmail}} immediately.'),
  },
  {
    key: 'receipt_transfer',
    name: 'Receipt: transfer',
    subject: 'Transfer sent, receipt {{reference}}',
    preheader: 'Your transfer is complete. Here is your receipt.',
    variables: ['amount', 'recipient', 'reference'],
    body:
      h1('Transfer sent') +
      p('Your money has been delivered. Here is your receipt.') +
      details([
        ['Amount', '{{amount}}', true],
        ['To', '{{recipient}}'],
        ['Reference', '{{reference}}'],
      ]) +
      muted('If you did not authorise this transfer, contact {{supportEmail}} immediately.'),
  },
  {
    key: 'receipt_funding',
    name: 'Receipt: wallet funding',
    subject: 'Wallet funded, receipt {{reference}}',
    preheader: 'Money has been added to your wallet.',
    variables: ['amount', 'method', 'reference'],
    body:
      h1('Wallet funded') +
      p('Your deposit has landed and is ready to spend.') +
      details([
        ['Amount', '{{amount}}', true],
        ['Method', '{{method}}'],
        ['Reference', '{{reference}}'],
      ]) +
      muted('Questions about this transaction? Contact {{supportEmail}} and quote the reference.'),
  },
  {
    key: 'receipt_giftcard',
    name: 'Receipt: gift card',
    subject: 'Gift card redeemed, receipt {{reference}}',
    preheader: 'Your gift card has been redeemed.',
    variables: ['brand', 'amount', 'reference'],
    body:
      h1('Gift card redeemed') +
      p('Your gift card was approved and credited to your naira wallet.') +
      details([
        ['Brand', '{{brand}}'],
        ['You received', '{{amount}}', true],
        ['Reference', '{{reference}}'],
      ]) +
      muted('Questions about this transaction? Contact {{supportEmail}} and quote the reference.'),
  },
  {
    key: 'support_reply',
    name: 'Support reply',
    subject: 'Re: your {{platformName}} support request',
    preheader: 'You have a new reply from our support team.',
    variables: ['agentName', 'message'],
    body:
      h1('A reply from support') +
      p('{{agentName}} from the {{platformName}} team replied to your conversation.') +
      quote('{{message}}') +
      button('View conversation', '{{websiteUrl}}') +
      muted('Reply from within the app to keep everything in one thread.'),
  },
  {
    key: 'missed_chat',
    name: 'Missed chat',
    subject: 'We missed you on chat',
    preheader: 'We could not reach you on live chat.',
    variables: [],
    body:
      h1('We missed you') +
      p(
        'You reached out on live chat while our agents were away. We would still love to help. Reply to this email or start a new chat any time.',
      ) +
      button('Continue the conversation', '{{websiteUrl}}'),
  },
  {
    key: 'security_alert',
    name: 'Security alert',
    subject: 'Security alert on your {{platformName}} account',
    preheader: 'We noticed activity on your account.',
    variables: ['event', 'device', 'time'],
    body:
      h1('New activity on your account') +
      p('We noticed the following activity on your account.') +
      details([
        ['Event', '{{event}}'],
        ['Device', '{{device}}'],
        ['When', '{{time}}'],
      ]) +
      p('If this was you, no action is needed. If it was not, secure your account now.') +
      button('Secure my account', '{{websiteUrl}}') +
      muted(
        'For your safety, {{platformName}} will never ask for your password or codes by email.',
      ),
  },
];

/**
 * The 12 default templates, exactly the key set the contract closes over.
 *
 * Every one is `category: 'transactional'` and `usesLayout: true`. The category is not a
 * judgement call: a receipt must reach somebody who unsubscribed from newsletters, and the
 * closed key set contains nothing a customer may opt out of. Marketing goes out as a
 * broadcast, which is a different surface precisely so that this one cannot send it.
 */
export function seedEmailTemplates(): EmailTemplate[] {
  return DEFS.map((d) => ({
    key: d.key,
    name: d.name,
    subject: d.subject,
    preheader: d.preheader,
    html: d.body,
    usesLayout: true,
    category: 'transactional' as const,
    variables: [...d.variables],
    enabled: true,
    updatedByAdminId: SUPERADMIN_ID,
    updatedAt: SEED_AT,
  }));
}

/** Plausible sample values for previews / test sends, per variable name. */
export const SAMPLE_VARS: Readonly<Record<string, string>> = {
  firstName: 'Amara',
  code: '481920',
  resetUrl: 'https://genex.pay/reset?token=demo',
  amount: '₦25,000.00',
  asset: '0.0042 BTC',
  reference: 'GX-8H2K91',
  fromAmount: '$100.00',
  toAmount: '₦158,400.00',
  bankName: 'GTBank ••34',
  recipient: 'Chidi O.',
  method: 'Bank transfer',
  brand: 'Amazon',
  agentName: 'Tobi',
  message: 'Thanks for reaching out. I have credited your wallet. Let me know if anything else comes up!',
  event: 'New sign-in',
  device: 'iPhone 15 · Lagos, NG',
  time: '30 Jul 2026, 14:22 WAT',
};

/**
 * The active email layout, version 1.
 *
 * The palette is the contract's `DEFAULT_EMAIL_PALETTE`, reached through the console's own
 * mirror rather than retyped. That matters more than it looks: every pair in it clears WCAG AA
 * against the background it is actually painted on, and the API refuses a save that does not.
 * A seed whose colours failed the validator would be worse than no seed, because it would look
 * like the sanctioned choice and the first save an operator made would be refused for colours
 * they never chose.
 *
 * The footer carries real copy and real links so the layout editor opens on a working example
 * rather than a blank form somebody has to guess the shape of.
 */
export function seedEmailLayout(): EmailLayout {
  return {
    version: 1,
    content: {
      senderName: 'GenEx Pay',
      replyTo: 'support@genex.pay',
      showLogo: true,
      logoWidthPx: DEFAULT_EMAIL_LOGO_WIDTH,
      ...DEFAULT_EMAIL_PALETTE,
      footerText: 'You are receiving this email because you have a GenEx Pay account.',
      // Deliberately a placeholder rather than a real registered name, address or licence
      // number. This is a demo seed; a legal disclosure that reads as genuine is one an
      // operator could ship without ever noticing they never wrote it.
      footerLegalText: 'Registered company name, address and licence number go here.',
      socialLinks: [
        { label: 'X', url: 'https://twitter.com/genexpay' },
        { label: 'Instagram', url: 'https://instagram.com/genexpay' },
      ],
      legalLinks: [
        { label: 'Terms', url: 'https://genex.pay/terms' },
        { label: 'Privacy', url: 'https://genex.pay/privacy' },
        { label: 'Help centre', url: 'https://genex.pay/help' },
      ],
    },
    updatedByAdminId: SUPERADMIN_ID,
    updatedAt: SEED_AT,
  };
}

/**
 * Two images in the library, so the picker opens on something.
 *
 * `url` is absolute, because that is the only kind of address a mail client will fetch: a
 * relative path means nothing in an inbox, and `data:` is refused outright by Gmail,
 * Outlook.com and Yahoo. The contract allows null here for a deployment with no public base
 * configured; the demo has one, so neither of these is null.
 */
export function seedEmailImages(): EmailImage[] {
  return [
    {
      id: 'e1000000-0000-4000-8000-000000000001',
      mime: 'image/png',
      url: 'https://dummyimage.com/1200x400/5b2ce6/ffffff.png&text=GenEx+Pay',
      byteSize: 48_210,
      width: 1200,
      height: 400,
      label: 'Newsletter header',
      altText: 'The GenEx Pay wordmark on a violet band',
      uploadedByAdminId: SUPERADMIN_ID,
      createdAt: SEED_AT,
    },
    {
      id: 'e1000000-0000-4000-8000-000000000002',
      mime: 'image/jpeg',
      url: 'https://dummyimage.com/1200x628/16c784/04150d.jpg&text=Refer+a+friend',
      byteSize: 132_640,
      width: 1200,
      height: 628,
      label: 'Referral campaign banner',
      altText: 'Refer a friend and you both earn a bonus',
      uploadedByAdminId: SUPERADMIN_ID,
      createdAt: SEED_AT,
    },
  ];
}

/**
 * Two broadcasts, in the two states the list page most needs to distinguish: one finished and
 * one not yet sent.
 *
 * The completed one carries a skipped count on purpose. Skipped is not failed: a broadcast
 * that declined to mail 40 customers who opted out of marketing worked exactly as intended,
 * and an operator who reads that number as a failure will go looking for a fault that is not
 * there.
 *
 * Returns the detail shape (the list shape plus `bodyHtml`) because the mock stores one row
 * per broadcast and the detail route has to be able to answer from it.
 */
export function seedEmailBroadcasts(): EmailBroadcastDetail[] {
  return [
    {
      id: 'b1000000-0000-4000-8000-000000000001',
      subject: 'Your naira wallet just got faster',
      preheader: 'Instant settlement is now on for every bank we support.',
      audience: { kind: 'verified' },
      category: 'announcement',
      status: 'completed',
      totalRecipients: 412,
      sentCount: 412,
      failedCount: 3,
      skippedCount: 40,
      createdByAdminId: SUPERADMIN_ID,
      createdAt: '2026-07-18T10:05:00.000Z',
      startedAt: '2026-07-18T10:06:12.000Z',
      finishedAt: '2026-07-18T10:11:48.000Z',
      bodyHtml:
        h1('Instant settlement, everywhere') +
        p(
          'Payouts to every bank we support now land in seconds rather than minutes. Nothing to turn on, it is already live on your account.',
        ) +
        button('Open {{platformName}}', '{{websiteUrl}}'),
    },
    {
      id: 'b1000000-0000-4000-8000-000000000002',
      subject: 'Refer a friend, you both earn',
      preheader: 'Share your code and earn on every friend who trades.',
      audience: { kind: 'tier', tier: 'tier_2' },
      category: 'marketing',
      status: 'draft',
      totalRecipients: 0,
      sentCount: 0,
      failedCount: 0,
      skippedCount: 0,
      createdByAdminId: SUPERADMIN_ID,
      createdAt: '2026-07-29T15:40:00.000Z',
      startedAt: null,
      finishedAt: null,
      bodyHtml:
        h1('Earn when your friends trade') +
        p(
          'Share your referral code. When a friend joins and makes their first trade, you both get a bonus in your naira wallet.',
        ) +
        button('Get my code', '{{websiteUrl}}'),
    },
  ];
}
